import { useCallback, useEffect, useState } from "react";

import ConfirmPanel from "../components/ConfirmPanel";
import {
  fetchMyConsents,
  fetchRealmConsentRequirements,
  grantConsent,
  revokeConsent,
} from "../lib/api";
import { describeApiError } from "../lib/apiError";
import {
  consentKey,
  isGenericSummary,
  isGranted,
  isUnreachableTermsUrl,
  missingConsents,
  unionRequirements,
  type RequirementWithRealms,
} from "../lib/consent";
import { useSession } from "../lib/session";
import type { ConsentRecord } from "../lib/types";

interface Pending {
  kind: "grant";
  req: RequirementWithRealms;
}
interface PendingRevoke {
  kind: "revoke";
  record: ConsentRecord;
  requirement: RequirementWithRealms | null;
}
type PendingAction = Pending | PendingRevoke | null;

function TermsLink({ url, purpose }: { url: string; purpose: string }) {
  const unreachable = isUnreachableTermsUrl(url, window.location.origin);
  return (
    <div className="space-y-1">
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="text-kw-l1 hover:underline break-all"
      >
        {url || `terms for ${purpose}`}
      </a>
      {unreachable && (
        <div className="text-amber-400">
          This link points at a localhost address and will not resolve from here — the operator
          must set <span className="font-mono">compliance.terms_doc_url_base</span>.
        </div>
      )}
    </div>
  );
}

/**
 * Grant and revoke the consents this subject needs.
 *
 * Self-scoped by construction: the server takes `subject_id` from the identity,
 * and an admin can neither grant nor read someone else's consent. That is the
 * right shape — a consent granted by a third party is not a consent. The
 * operator-side counterpart is the DSR pipeline, not this panel.
 */
export default function ConsentPanel() {
  const { grants } = useSession();
  const [requirements, setRequirements] = useState<RequirementWithRealms[]>([]);
  const [records, setRecords] = useState<ConsentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pending, setPending] = useState<PendingAction>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState("");

  // The subject's own realms — NOT `GET /v1/me/realms`, which widens to every
  // realm of the deployment for a platform-admin (one client-assets call each).
  const myRealms = grants.realms;

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [perRealm, active] = await Promise.all([
        Promise.all(
          myRealms.map(async (realmId) => ({
            realmId,
            reqs: await fetchRealmConsentRequirements(realmId).catch(() => []),
          })),
        ),
        fetchMyConsents(),
      ]);
      setRequirements(unionRequirements(perRealm));
      setRecords(active);
    } catch (e: unknown) {
      setError(describeApiError(e).message);
    } finally {
      setLoading(false);
    }
  }, [myRealms]);

  useEffect(() => {
    void load();
  }, [load]);

  const outstanding = missingConsents(requirements, records);

  async function confirmAction() {
    if (!pending) return;
    setActionError("");
    setBusy(true);
    try {
      if (pending.kind === "grant") {
        // A grant is a plain INSERT with no uniqueness on (subject, pack, purpose):
        // a double submit would create two active rows. Re-check against the
        // freshly loaded delta, and reload before the button is live again.
        if (!isGranted(pending.req, records)) {
          await grantConsent(pending.req);
        }
      } else {
        await revokeConsent(pending.record.consent_id);
      }
      setPending(null);
      await load();
    } catch (e: unknown) {
      setActionError(describeApiError(e).message);
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="text-sm text-kw-text-muted">Loading consents…</div>;

  return (
    <div className="space-y-4">
      {error && <div className="text-sm text-red-400">{error}</div>}

      {myRealms.length === 0 && (
        <div className="text-xs text-kw-text-faint">
          You are not a member of any realm, so no pack requires an acknowledgement from you.
        </div>
      )}

      {outstanding.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-xs uppercase tracking-wide text-amber-400">
            Outstanding ({outstanding.length})
          </h4>
          {outstanding.map((req) => {
            const isPending = pending?.kind === "grant" && consentKey(pending.req) === consentKey(req);
            return (
              <div
                key={consentKey(req)}
                className="border border-kw-border-subtle rounded p-3 space-y-2 text-xs"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-kw-text-primary">{req.pack_id}</span>
                  <span className="text-kw-text-faint">·</span>
                  <span className="font-mono text-kw-text-muted">{req.purpose}</span>
                </div>
                <div className="text-kw-text-faint">
                  required by {req.realms.join(", ")}
                </div>
                {!isPending && (
                  <button
                    onClick={() => {
                      setActionError("");
                      setPending({ kind: "grant", req });
                    }}
                    className="text-kw-l1 hover:underline"
                  >
                    Review and acknowledge ▸
                  </button>
                )}
                {isPending && (
                  <ConfirmPanel
                    title={`Acknowledge ${req.purpose}`}
                    requireAck="I have read the terms and acknowledge them."
                    confirmLabel="Acknowledge"
                    busy={busy}
                    error={actionError}
                    onConfirm={confirmAction}
                    onCancel={() => setPending(null)}
                  >
                    <p className={isGenericSummary(req.summary, req.purpose) ? "italic" : ""}>
                      {req.summary}
                      {isGenericSummary(req.summary, req.purpose) && (
                        <span className="text-kw-text-faint">
                          {" "}
                          (generic placeholder — no summary is configured for this purpose; read
                          the terms document)
                        </span>
                      )}
                    </p>
                    <TermsLink url={req.terms_doc_url} purpose={req.purpose} />
                  </ConfirmPanel>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="space-y-3">
        <h4 className="text-xs uppercase tracking-wide text-kw-text-muted">
          Active ({records.length})
        </h4>
        {records.length === 0 ? (
          <div className="text-xs text-kw-text-faint">No active consents.</div>
        ) : (
          <ul className="space-y-2 text-xs">
            {records.map((rec) => {
              const isPending = pending?.kind === "revoke" && pending.record.consent_id === rec.consent_id;
              const requirement =
                requirements.find((r) => consentKey(r) === consentKey(rec)) ?? null;
              return (
                <li
                  key={rec.consent_id}
                  className="border border-kw-border-subtle rounded p-3 space-y-2"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-kw-text-primary">{rec.pack_id}</span>
                    <span className="text-kw-text-faint">·</span>
                    <span className="font-mono text-kw-text-muted">{rec.purpose}</span>
                    <span className="text-kw-text-faint">
                      granted {rec.granted_at.slice(0, 19).replace("T", " ")}
                    </span>
                  </div>
                  {!isPending && (
                    <button
                      onClick={() => {
                        setActionError("");
                        setPending({ kind: "revoke", record: rec, requirement });
                      }}
                      className="text-kw-text-muted hover:text-amber-400"
                    >
                      Revoke
                    </button>
                  )}
                  {isPending && (
                    <ConfirmPanel
                      title={`Revoke ${rec.purpose}?`}
                      tone="danger"
                      requireAck="I understand what stops working."
                      confirmLabel="Revoke"
                      busy={busy}
                      error={actionError}
                      onConfirm={confirmAction}
                      onCancel={() => setPending(null)}
                    >
                      {requirement ? (
                        <p>
                          <span className="font-mono">{rec.pack_id}</span> requires this
                          acknowledgement, so a pack-narrowed ingest against it will be refused
                          with 403 until you acknowledge again.
                        </p>
                      ) : (
                        <p>
                          No pack in your realms currently requires this purpose — revoking it
                          should not affect ingest.
                        </p>
                      )}
                      <p className="text-kw-text-faint">
                        Revoking keeps the record for the audit trail and writes a new one if you
                        acknowledge again.
                      </p>
                    </ConfirmPanel>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
